export const MEGABYTE = 1_000_000;
export const MEBIBYTE = 1024 * 1024;

export const PROCESSING_CHUNK_BYTES = 40 * MEBIBYTE;
export const PROCESSING_CHUNK_PAGES = 50;
export const MAX_IMAGE_BYTES = 20 * MEGABYTE;
// Cloud Run HTTP/1 rejects requests above 32 MiB before Django can inspect
// them. These values select multipart vs. resumable transport; they are not
// document-policy limits.
export const DIRECT_MULTIPART_MAX_FILE_BYTES = 20 * MEBIBYTE;
export const DIRECT_MULTIPART_MAX_TOTAL_BYTES = 30 * MEBIBYTE;

export interface UploadPolicy {
  readonly key: string;
  readonly max_file_bytes: number;
  readonly max_total_bytes: number;
  readonly min_files: number;
  readonly max_files: number;
  readonly processing_chunk_bytes: number;
  readonly processing_chunk_pages: number;
  readonly chunk_processing: boolean;
  readonly max_image_bytes: number;
}

type PolicyOptions = {
  readonly key: string;
  readonly maxFileMB: number;
  readonly maxTotalMB: number;
  readonly maxFiles: number;
  readonly chunkProcessing: boolean;
  readonly minFiles?: number;
};

function policy({
  key,
  maxFileMB,
  maxTotalMB,
  maxFiles,
  chunkProcessing,
  minFiles = 1,
}: PolicyOptions): UploadPolicy {
  return Object.freeze({
    key,
    max_file_bytes: maxFileMB * MEGABYTE,
    max_total_bytes: maxTotalMB * MEGABYTE,
    min_files: minFiles,
    max_files: maxFiles,
    processing_chunk_bytes: PROCESSING_CHUNK_BYTES,
    processing_chunk_pages: PROCESSING_CHUNK_PAGES,
    chunk_processing: chunkProcessing,
    max_image_bytes: MAX_IMAGE_BYTES,
  });
}

// Keep this matrix aligned with apps/docvault/upload_policies.py in Django.
export const UPLOAD_POLICIES = Object.freeze({
  identitySingleSide: policy({
    key: "identity",
    maxFileMB: 20,
    maxTotalMB: 20,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  identityTwoSide: policy({
    key: "identity",
    maxFileMB: 20,
    maxTotalMB: 40,
    maxFiles: 2,
    chunkProcessing: false,
  }),
  proofOfAddress: policy({
    key: "proof_of_address",
    maxFileMB: 20,
    maxTotalMB: 20,
    maxFiles: 1,
    chunkProcessing: true,
  }),
  proofOfAddressLarge: policy({
    key: "proof_of_address",
    maxFileMB: 25,
    maxTotalMB: 25,
    maxFiles: 1,
    chunkProcessing: true,
  }),
  bankStatement: policy({
    key: "bank_statement",
    maxFileMB: 25,
    maxTotalMB: 300,
    minFiles: 3,
    maxFiles: 12,
    chunkProcessing: true,
  }),
  bankStatementReplacement: policy({
    key: "bank_statement",
    maxFileMB: 25,
    maxTotalMB: 300,
    maxFiles: 12,
    chunkProcessing: true,
  }),
  payrollReceipt: policy({
    key: "payroll_receipt",
    maxFileMB: 20,
    maxTotalMB: 120,
    maxFiles: 15,
    chunkProcessing: false,
  }),
  taxDeclaration: policy({
    key: "tax_declaration",
    maxFileMB: 50,
    maxTotalMB: 100,
    maxFiles: 2,
    chunkProcessing: false,
  }),
  proofOfIncome: policy({
    key: "proof_of_income",
    maxFileMB: 20,
    maxTotalMB: 20,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  constitutiveAct: policy({
    key: "constitutive_act",
    maxFileMB: 250,
    maxTotalMB: 900,
    maxFiles: 6,
    chunkProcessing: true,
  }),
  predial: policy({
    key: "predial",
    maxFileMB: 25,
    maxTotalMB: 25,
    maxFiles: 1,
    chunkProcessing: true,
  }),
  propertyDeedManualReview: policy({
    key: "property_deed_manual_review",
    maxFileMB: 250,
    maxTotalMB: 250,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  powerOfAttorney: policy({
    key: "power_of_attorney",
    maxFileMB: 50,
    maxTotalMB: 50,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  boletaRegistral: policy({
    key: "boleta_registral",
    maxFileMB: 250,
    maxTotalMB: 250,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  satCompliance: policy({
    key: "sat_compliance",
    maxFileMB: 20,
    maxTotalMB: 20,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  taxStatusCertificate: policy({
    key: "tax_status_certificate",
    maxFileMB: 20,
    maxTotalMB: 20,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  authorizationLetter: policy({
    key: "authorization_letter",
    maxFileMB: 20,
    maxTotalMB: 20,
    maxFiles: 1,
    chunkProcessing: false,
  }),
  simple: policy({
    key: "simple",
    maxFileMB: 25,
    maxTotalMB: 25,
    maxFiles: 1,
    chunkProcessing: false,
  }),
});

// Compatibility aliases for upload components that accept limits in MB.
// Derive them from the policy table so the numeric source remains unique.
export const IDENTITY_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.identityTwoSide.max_file_bytes / MEGABYTE;
export const SIMPLE_DOCUMENT_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.proofOfIncome.max_file_bytes / MEGABYTE;
export const BANK_STATEMENT_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.bankStatement.max_file_bytes / MEGABYTE;
export const TAX_DECLARATION_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.taxDeclaration.max_file_bytes / MEGABYTE;
export const PREDIAL_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.predial.max_file_bytes / MEGABYTE;
export const POWER_OF_ATTORNEY_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.powerOfAttorney.max_file_bytes / MEGABYTE;
export const BOLETA_REGISTRAL_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.boletaRegistral.max_file_bytes / MEGABYTE;
export const LARGE_DOCUMENT_MAX_FILE_SIZE_MB =
  UPLOAD_POLICIES.constitutiveAct.max_file_bytes / MEGABYTE;

const SHARED_TWENTY_MEGABYTE_POLICIES: Readonly<Record<string, UploadPolicy>> =
  Object.freeze({
    "sat-compliance": UPLOAD_POLICIES.satCompliance,
    "tax-status-certificate": UPLOAD_POLICIES.taxStatusCertificate,
    "authorization-letter": UPLOAD_POLICIES.authorizationLetter,
  });

export type UploadPolicyContext = {
  readonly requirementSlug?: string | null;
  readonly documentType?: string | null;
  readonly requirementStatus?: string | null;
};

export function getDocumentUploadPolicy({
  requirementSlug = "",
  documentType = "",
  requirementStatus = "",
}: UploadPolicyContext): UploadPolicy {
  const slug = requirementSlug ?? "";
  const type = documentType ?? "";
  const status = requirementStatus ?? "";

  if (slug === "official-id") {
    return type === "passport"
      ? UPLOAD_POLICIES.identitySingleSide
      : UPLOAD_POLICIES.identityTwoSide;
  }

  if (slug === "proof-of-address" || slug === "comprobante-domicilio") {
    return type === "bank_statement" || type === "predial"
      ? UPLOAD_POLICIES.proofOfAddressLarge
      : UPLOAD_POLICIES.proofOfAddress;
  }

  if (slug === "proof-of-income") {
    if (type === "bank_statement") {
      return status === "invalid"
        ? UPLOAD_POLICIES.bankStatementReplacement
        : UPLOAD_POLICIES.bankStatement;
    }
    if (type === "payroll_receipt") {
      return UPLOAD_POLICIES.payrollReceipt;
    }
    if (type === "tax_declaration") {
      return UPLOAD_POLICIES.taxDeclaration;
    }
    return UPLOAD_POLICIES.proofOfIncome;
  }

  if (slug === "constitutive-act") {
    return UPLOAD_POLICIES.constitutiveAct;
  }

  if (
    slug === "predial" ||
    (slug === "escrituras-predial" && type === "predial")
  ) {
    return UPLOAD_POLICIES.predial;
  }

  if (
    slug === "escrituras-predial" &&
    (type === "escrituras" || type === "scriptures")
  ) {
    return UPLOAD_POLICIES.propertyDeedManualReview;
  }

  if (slug === "power-of-attorney") {
    return UPLOAD_POLICIES.powerOfAttorney;
  }

  if (slug === "boleta-registral") {
    return UPLOAD_POLICIES.boletaRegistral;
  }

  return SHARED_TWENTY_MEGABYTE_POLICIES[slug] ?? UPLOAD_POLICIES.simple;
}
